import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import type { TPatient } from 'src/types/patient'
import type { TDashboardPatientMedEvent } from './dashboard.type'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DashboardService } from 'src/services/dashboard.service'
import { CurecallBanner } from '@honestica/curecall-ui'
import type { TPatientMedEventStatus } from 'src/types/med-event'
import { useParams } from 'react-router'
import { useLocale } from 'src/hooks/use-locale'
import { useSocket } from 'src/providers/socket.provider'
import { localizeObject } from 'src/utils/localize-object.util'

type TDashboardContext = {
  dashboardPatientMedEventList: TDashboardPatientMedEvent[]
  dashboardPatientMedEventListToShow: TDashboardPatientMedEvent[]
  setDashboardPatientMedEventListToShow: Dispatch<SetStateAction<TDashboardPatientMedEvent[]>>
  dashboardMedEventIds: number[]
  selectedPatientMedEvent: TDashboardPatientMedEvent | null
  setSelectedPatientMedEvent: (patientMedEvent: TDashboardPatientMedEvent) => void
}
const DashboardContext = createContext<TDashboardContext>({
  dashboardPatientMedEventList: [],
  dashboardPatientMedEventListToShow: [],
  setDashboardPatientMedEventListToShow: () => {},
  dashboardMedEventIds: [],
  selectedPatientMedEvent: null,
  setSelectedPatientMedEvent: () => {},
})

export const useDashboard = () => useContext(DashboardContext)

const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const projectIdNum = Number(projectId)
  const queryClient = useQueryClient()
  const { socket, socketError } = useSocket()
  const { locale: currentLocale } = useLocale()

  const [dashboardPatientMedEventListToShow, setDashboardPatientMedEventListToShow] = useState<
    TDashboardPatientMedEvent[]
  >([])
  const [selectedPatientMedEvent, setSelectedPatientMedEvent] = useState<TDashboardPatientMedEvent | null>(
    null,
  )
  const selectedPatientId = Number(selectedPatientMedEvent?.Patient?.id)
  const selectedPatientMedEventId = Number(selectedPatientMedEvent?.id)

  const {
    isLoading: isDashboardPatientMedEventListLoading,
    isError: isDashboardPatientMedEventListError,
    data,
    refetch,
  } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => DashboardService.get(projectIdNum),
    enabled: !!projectIdNum,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * (60 * 1000), // 5 mins
  })

  const setSelectNewPatiendMedEvent = useCallback(
    (patientMedEvent: TDashboardPatientMedEvent) => {
      setSelectedPatientMedEvent(patientMedEvent)
      localStorage.setItem('dashboardSelectedPatientMedEventId', patientMedEvent.id.toString())
    },
    [setSelectedPatientMedEvent],
  )

  const onSocketPatientUpdated = useCallback(
    (data: { patient: TPatient }) => {
      const patientToUpdate = localizeObject(data.patient, currentLocale as any)
      if (patientToUpdate.id === selectedPatientId) {
        const selectedPatientMedEventToUpdate = patientToUpdate.PatientMedEvents.find(
          (patientMedEvent: any) => {
            return patientMedEvent.id === selectedPatientMedEventId
          },
        )
        if (selectedPatientMedEventToUpdate?.id) {
          queryClient.setQueryData(
            ['dashboard-patient-med-event', selectedPatientMedEventId],
            (oldData: any) => {
              if (!oldData) return
              const result = {
                patient: { ...oldData.patient, ...patientToUpdate },
                patientMedEvent: { ...oldData.patientMedEvent, ...selectedPatientMedEventToUpdate },
                patientMedAlerts: patientToUpdate.PatientMedAlerts,
                medicalTeamComments: oldData.medicalTeamComments,
              }
              return result
            },
          )
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPatientId, selectedPatientMedEventId],
  )
  const onSocketPatientMedEventStatusUpdated = useCallback(
    ({ patientMedEventId, newStatus }: { patientMedEventId: number; newStatus: TPatientMedEventStatus }) => {
      refetch()
      queryClient.setQueryData(['dashboard-patient-med-event', patientMedEventId], (oldData: any) => {
        if (!oldData) return
        const updatedDashboardPatientMedEvent = {
          ...oldData,
          patientMedEvent: {
            ...oldData.patientMedEvent,
            status: newStatus,
          },
        }
        return updatedDashboardPatientMedEvent
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  useEffect(() => {
    if (!socket || !selectedPatientId || socketError) return undefined
    socket?.emit('SUBSCRIBE_PATIENT', { patientId: selectedPatientId })
    socket?.on('PATIENT_MED_EVENT_STATUS', onSocketPatientMedEventStatusUpdated)
    socket?.on('PATIENT_UPDATED', onSocketPatientUpdated)
    return () => {
      socket?.emit('UNSUBSCRIBE_PATIENT', { patientId: selectedPatientId })
      socket?.off('PATIENT_MED_EVENT_STATUS', onSocketPatientMedEventStatusUpdated)
      socket?.off('PATIENT_UPDATED', onSocketPatientUpdated)
    }
  }, [onSocketPatientMedEventStatusUpdated, onSocketPatientUpdated, selectedPatientId, socketError, socket])

  const value = useMemo(
    () => ({
      dashboardPatientMedEventList: data?.dashboardPatientMedEvents || [],
      dashboardPatientMedEventListToShow,
      setDashboardPatientMedEventListToShow,
      dashboardMedEventIds: data?.dashboardMedEventIds || [],
      selectedPatientMedEvent,
      setSelectedPatientMedEvent: setSelectNewPatiendMedEvent,
    }),
    [
      data?.dashboardPatientMedEvents,
      dashboardPatientMedEventListToShow,
      setDashboardPatientMedEventListToShow,
      data?.dashboardMedEventIds,
      selectedPatientMedEvent,
      setSelectNewPatiendMedEvent,
    ],
  )

  if (isDashboardPatientMedEventListError)
    return <CurecallBanner className="c-h-max">{t('pages.dashboard.dashboard-banner')}</CurecallBanner>

  if (isDashboardPatientMedEventListLoading) return <div className="loading" />

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
}

export default DashboardProvider
